import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import AgentDialer from '../components/AgentDialer';
import CallDetailsForm from '../components/CallDetailsForm';
import { paymentsAPI, endUserAPI, callLogsAPI } from '../services/api';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { DashboardHeader } from '../components/user-dashboard/DashboardHeader';
import { PaymentStatusBanner } from '../components/user-dashboard/PaymentStatusBanner';
import { NotificationPromptBanner } from '../components/user-dashboard/NotificationPromptBanner';
import { SoftphoneSetupCard } from '../components/user-dashboard/SoftphoneSetupCard';
import { DashboardStatsRow } from '../components/user-dashboard/DashboardStatsRow';
import { VoiceAgentsList } from '../components/user-dashboard/VoiceAgentsList';
import { RecentCallsList } from '../components/user-dashboard/RecentCallsList';
import { TestCallModal } from '../components/user-dashboard/TestCallModal';
import { UpgradePlanModal } from '../components/user-dashboard/UpgradePlanModal';

const UserDashboard = () => {
  const { endUser, userLogout, userLoading } = useAuth();
  const { t } = useLanguage();
  const { playSound, requestNotificationPermission, notificationPermission, supportsNotifications } = useNotificationSound();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [agents, setAgents] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [showTestCall, setShowTestCall] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [currentCallId, setCurrentCallId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [lastCallData, setLastCallData] = useState(null);
  const [sipCredentials, setSipCredentials] = useState(null);
  const [isAvailable, setIsAvailable] = useState(true);
  const [availabilityUpdating, setAvailabilityUpdating] = useState(false);
  const [showZoiperSetup, setShowZoiperSetup] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  // Notification permission prompt timer
  useEffect(() => {
    if (supportsNotifications && notificationPermission === 'default') {
      const timer = setTimeout(() => {
        setShowNotificationPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [supportsNotifications, notificationPermission]);

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    setShowNotificationPrompt(false);
    if (granted) playSound('success');
  };

  const fetchUserData = async () => {
    try {
      const response = await endUserAPI.getProfile(endUser?.id);
      setUserData(response.data.user);
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await endUserAPI.getAgents(endUser?.id);
      setAgents(response.data.agents || []);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const fetchCallHistory = async () => {
    try {
      const response = await callLogsAPI.getHistory(endUser?.id);
      setCallHistory(response.data.calls || []);
    } catch (err) {
      console.error('Error fetching call history:', err);
    }
  };

  const fetchSipCredentials = async () => {
    let sipData = null;
    try {
      const response = await endUserAPI.getSipCredentials(endUser?.id);
      if (response.data.success) sipData = response.data;
    } catch (err) {
      console.error('Error fetching SIP credentials:', err);
    }

    try {
      const tokenResponse = await endUserAPI.getWebRTCToken(endUser?.id);
      if (tokenResponse.data.success && tokenResponse.data.token) {
        setSipCredentials({
          ...sipData,
          login_token: tokenResponse.data.token,
          username: endUser?.username || endUser?.name,
          name: endUser?.name,
          telnyx_phone_number: tokenResponse.data.phone_number || sipData?.telnyx_phone_number,
        });
        await endUserAPI.registerWebRTC(endUser?.id, {});
        return;
      }
    } catch (tokenErr) {
      console.log('WebRTC token generation failed, using SIP credentials only:', tokenErr);
    }

    if (sipData) {
      setSipCredentials(sipData);
      try {
        await endUserAPI.registerWebRTC(endUser?.id, {});
      } catch (e) {
        console.log('WebRTC registration failed:', e);
      }
    }
  };

  const fetchAvailabilityStatus = async () => {
    try {
      const response = await endUserAPI.getAvailability(endUser?.id);
      if (response.data.success) setIsAvailable(response.data.is_available);
    } catch (err) {
      console.error('Error fetching availability:', err);
      setIsAvailable(true);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setPaymentMessage('Payment verification timed out. Your subscription may take a few minutes to update.');
      return;
    }

    try {
      const response = await paymentsAPI.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPaymentMessage('Payment successful! Your plan has been upgraded.');
        fetchUserData();
        setTimeout(() => setSearchParams({}), 3000);
      } else if (response.data.status === 'expired') {
        setPaymentStatus('failed');
        setPaymentMessage('Payment session expired. Please try again.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setPaymentMessage('Error verifying payment. Please check your account.');
    }
  };

  // Check for payment return
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (payment === 'success' && sessionId && endUser) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- payment-return banner is intentionally side-effecting from URL params
      setPaymentStatus('processing');
      setPaymentMessage('Verifying your payment...');
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, endUser]);

  useEffect(() => {
    if (userLoading) return;
    if (!endUser) {
      navigate('/login');
      return;
    }

    // All bootstrap fetches as IIFEs so setState calls happen async and
    // not synchronously inside this effect's body.
    (async () => {
      await fetchUserData();
    })();
    (async () => { await fetchAgents(); })();
    (async () => { await fetchCallHistory(); })();
    (async () => { await fetchSipCredentials(); })();
    (async () => { await fetchAvailabilityStatus(); })();
  }, [endUser, navigate, userLoading]);

  useEffect(() => {
    let interval;
    if (isCallActive) {
      interval = setInterval(() => setCallDuration((prev) => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isCallActive]);

  const toggleAvailability = async () => {
    if (availabilityUpdating) return;
    setAvailabilityUpdating(true);
    const newStatus = !isAvailable;
    try {
      const response = await endUserAPI.setAvailability(endUser?.id, newStatus);
      if (response.data.success) {
        setIsAvailable(newStatus);
        playSound(newStatus ? 'success' : 'toggle');
      }
    } catch (err) {
      console.error('Error updating availability:', err);
      playSound('error');
    } finally {
      setAvailabilityUpdating(false);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleLogout = () => {
    userLogout();
    navigate('/login');
  };

  const startTestCall = (agent) => {
    setSelectedAgent(agent);
    setShowTestCall(true);
    setCallDuration(0);
    setPhoneNumber('');
    setChatMessages([{ role: 'agent', text: agent.greeting || 'Hello! How can I help you today?' }]);
  };

  const initiateCall = async () => {
    if (!phoneNumber || !selectedAgent) return;
    try {
      const response = await callLogsAPI.makeOutbound(endUser?.id, phoneNumber, selectedAgent.id);
      if (response.data.success) {
        setCurrentCallId(response.data.call.id);
        setIsCallActive(true);
        setChatMessages((prev) => [...prev, { role: 'system', text: 'Call initiated (Demo Mode)' }]);
        fetchUserData();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to initiate call';
      setChatMessages((prev) => [...prev, { role: 'system', text: `Error: ${errorMsg}` }]);
    }
  };

  const toggleCall = async () => {
    if (isCallActive) {
      try {
        await callLogsAPI.control(endUser?.id, currentCallId, 'hangup');
        setIsCallActive(false);
        setChatMessages((prev) => [...prev, { role: 'system', text: 'Call ended' }]);
        setCurrentCallId(null);
        fetchUserData();
        fetchCallHistory();
      } catch (err) {
        console.error('Error ending call:', err);
      }
    } else {
      initiateCall();
    }
  };

  const toggleMute = async () => {
    if (!currentCallId) return;
    try {
      await callLogsAPI.control(endUser?.id, currentCallId, isMuted ? 'unmute' : 'mute');
      setIsMuted(!isMuted);
    } catch (err) {
      console.error('Error toggling mute:', err);
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    setChatMessages((prev) => [...prev, { role: 'user', text: chatInput }]);
    setTimeout(() => {
      setChatMessages((prev) => [...prev, {
        role: 'agent',
        text: "I understand. Let me help you with that. Is there anything specific you'd like to know?",
      }]);
    }, 1000);
    setChatInput('');
  };

  const closeTestCall = () => {
    setShowTestCall(false);
    setIsCallActive(false);
    setCallDuration(0);
    setCurrentCallId(null);
  };

  const minutesRemaining = userData?.minutes_remaining || 0;
  const minutesUsed = userData?.minutes_used || 0;
  const planName = userData?.plan_name || 'Free Trial';

  // While auth/data loading, render minimal placeholder to avoid flicker
  if (loading && !userData) {
    // Keep render light — main effects will populate state.
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <DashboardHeader
        minutesRemaining={minutesRemaining}
        userName={endUser?.name}
        onLogout={handleLogout}
      />

      <main className="max-w-7xl mx-auto px-6 py-8" data-testid="user-dashboard">
        <PaymentStatusBanner
          status={paymentStatus}
          message={paymentMessage}
          onDismiss={() => setPaymentStatus(null)}
        />

        {showNotificationPrompt && (
          <NotificationPromptBanner
            t={t}
            onEnable={handleEnableNotifications}
            onDismiss={() => setShowNotificationPrompt(false)}
          />
        )}

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-2">
            {t('userDashboard.welcome')}, {endUser?.name || 'User'}
          </h1>
          <p className="text-gray-400">{t('userDashboard.welcomeSubtitle')}</p>
        </div>

        <SoftphoneSetupCard
          sipCredentials={sipCredentials}
          copiedField={copiedField}
          onCopy={copyToClipboard}
          showInstructions={showZoiperSetup}
          onToggleInstructions={() => setShowZoiperSetup((prev) => !prev)}
          t={t}
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
          <div>
            <AgentDialer
              userId={endUser?.id}
              userName={endUser?.name}
              onCallStart={(call) => console.log('Call started:', call)}
              onCallEnd={(data) => {
                console.log('Call ended:', data);
                if (data.callData) setLastCallData(data.callData);
                fetchCallHistory();
              }}
            />
          </div>
          <div>
            <CallDetailsForm
              callData={lastCallData}
              transcript={lastCallData?.transcript || ''}
              summary={lastCallData?.summary || ''}
              aiAgentName={lastCallData?.agentName || ''}
              userId={endUser?.id}
              onSave={async (formData) => {
                try {
                  await callLogsAPI.submitDetails(formData, {
                    headers: { 'X-User-Id': endUser?.id },
                  });
                  setLastCallData(null);
                  return true;
                } catch (error) {
                  console.error('Error saving call details:', error);
                  throw error;
                }
              }}
            />
          </div>
        </div>

        <DashboardStatsRow
          isAvailable={isAvailable}
          availabilityUpdating={availabilityUpdating}
          onToggleAvailability={toggleAvailability}
          callsToday={callHistory.length}
          minutesUsed={minutesUsed}
          planName={planName}
          t={t}
        />

        <VoiceAgentsList agents={agents} onTestCall={startTestCall} t={t} />

        <RecentCallsList callHistory={callHistory} t={t} />
      </main>

      {showTestCall && selectedAgent && (
        <TestCallModal
          agent={selectedAgent}
          isCallActive={isCallActive}
          callDuration={callDuration}
          phoneNumber={phoneNumber}
          chatMessages={chatMessages}
          chatInput={chatInput}
          isMuted={isMuted}
          minutesRemaining={minutesRemaining}
          onPhoneNumberChange={setPhoneNumber}
          onChatInputChange={setChatInput}
          onSendMessage={sendMessage}
          onToggleCall={toggleCall}
          onToggleMute={toggleMute}
          onClose={closeTestCall}
          onUpgrade={() => { setShowTestCall(false); setShowUpgrade(true); }}
        />
      )}

      {showUpgrade && (
        <UpgradePlanModal
          onClose={() => setShowUpgrade(false)}
          onSelectPlan={() => navigate('/pricing')}
          onViewAllPlans={() => navigate('/pricing')}
        />
      )}
    </div>
  );
};

export default UserDashboard;
